import { useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { StreamingStatusDTO } from 'core/streamingStatus/StreamingStatus';
import {
  DefaultStreamingStatusManager,
  StreamingStatusManager
} from 'core/streamingStatus/StreamingStatusManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useParams } from 'react-router-dom';

export type StreamingStatusFormData = {
  crid: number | '';
  isLive: boolean;
};

export type StreamingStatusFormModelData = {
  loading: boolean;
  title: string;
  isEditing: boolean;
  redirectPathData?: {
    pathname: string;
    state?: { needRefresh?: boolean };
  };
  initFormData?: StreamingStatusFormData;
  submit: (formData: StreamingStatusFormData) => void;
  cancel: () => void;
};

const manager: StreamingStatusManager = new DefaultStreamingStatusManager();

export const useStreamingStatusCreateFormModel =
  (): StreamingStatusFormModelData => {
    const { loading, callAPIs } = useCallAPI();
    const [redirectPathData, setRedirectPathData] = useState<{
      pathname: string;
      state?: { needRefresh?: boolean };
    }>();

    const initFormData = useMemo<StreamingStatusFormData>(
      () => ({ crid: '', isLive: false }),
      []
    );

    const backToList = useCallback(() => {
      setRedirectPathData({
        pathname: '/system/streaming-status',
        state: { needRefresh: true }
      });
    }, []);

    const submit = useCallback(
      (formData: StreamingStatusFormData) => {
        callAPIs(
          [() => manager.updateStreamingStatus(formData.crid, formData.isLive)],
          () => {
            backToList();
          }
        );
      },
      [backToList, callAPIs]
    );

    return {
      loading,
      title: i18n.t('streamingStatusForm.labels.createTitle'),
      isEditing: false,
      redirectPathData,
      initFormData,
      submit,
      cancel: backToList
    };
  };

export const useStreamingStatusEditFormModel = (
  streamingStatusList: StreamingStatusDTO[]
): StreamingStatusFormModelData => {
  const params = useParams<{ crid: string }>();
  const crid = Number(decodeURIComponent(params.crid));
  const { loading, callAPIs } = useCallAPI();
  const [redirectPathData, setRedirectPathData] = useState<{
    pathname: string;
    state?: { needRefresh?: boolean };
  }>();

  const initFormData = useMemo<StreamingStatusFormData | undefined>(() => {
    const found = streamingStatusList.find((item) => item.crid === crid);
    if (found) {
      return { crid: found.crid, isLive: found.isLive };
    }
    return undefined;
  }, [crid, streamingStatusList]);

  const backToList = useCallback(() => {
    setRedirectPathData({
      pathname: '/system/streaming-status',
      state: { needRefresh: true }
    });
  }, []);

  const submit = useCallback(
    (formData: StreamingStatusFormData) => {
      callAPIs(
        [() => manager.updateStreamingStatus(formData.crid, formData.isLive)],
        () => {
          backToList();
        }
      );
    },
    [backToList, callAPIs]
  );

  return {
    loading,
    title: i18n.t('streamingStatusForm.labels.editTitle'),
    isEditing: true,
    redirectPathData,
    initFormData,
    submit,
    cancel: backToList
  };
};
